import {
	Box,
	Button,
	CardActions,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useGetCountriesQuery } from '@/api/common';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import avatar from '@/assets/images/avatar.png';
import { useFileUploader } from '@/hooks/useFileUploader';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { FileInfo } from '@/api/DTO/common.interface';
import { UserRequest } from '@/api/DTO/user/userList.interface';
import { useCreateUserMutation } from '@/api/user/userApiSlice';
import {
	APP_USER_PASSWORD_REQUIREMENT_REG_EXP,
	APP_USER_PASSWORD_RESTRICT_REG_EXP,
} from '@/common/constants/pattern';

interface StallInfoFormProps {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function UserInfoForm({ type }: StallInfoFormProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const { uploadFile } = useFileUploader();
	const [avatarFileObj, setAvatarFileObj] = useState<FileInfo | null>(null);
	const [createUser] = useCreateUserMutation();

	const ageList = [
		{ id: 0, name: '<25' },
		{ id: 1, name: '25-34' },
		{ id: 2, name: '35-44' },
		{ id: 3, name: '45-54' },
		{ id: 4, name: '>54' },
	];
	const sexList = [
		{ id: 0, name: t('user.boy') },
		{ id: 1, name: t('user.girl') },
		{ id: 2, name: t('user.guarded') },
	];

	const { data: countryList }: any = useGetCountriesQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<UserRequest> = {
		receivePromotion: true,
		phone: '',
		email: '',
	};

	const formSchema = Yup.object().shape(
		{
			nickname: Yup.string()
				.required(t('errorMessage.required'))
				.min(1, t('errorMessage.min3'))
				.max(10, t('errorMessage.max100')),
			password: Yup.string()
				.required(t('errorMessage.required'))
				.matches(
					APP_USER_PASSWORD_REQUIREMENT_REG_EXP,
					t('errorMessage.app_user_password_requirement')
				)
				.matches(
					APP_USER_PASSWORD_RESTRICT_REG_EXP,
					t('errorMessage.app_user_password_restrict')
				),

			email: Yup.string()
				.nullable()
				.when('phone', {
					is: (phone: string) => !phone,
					then: (schema) => schema.required(t('errorMessage.emailOrPhone')),
				})
				.email(t('errorMessage.email')),
			phone: Yup.string()
				.nullable()
				.when('email', {
					is: (email: string) => !email,
					then: (schema) => schema.required(t('errorMessage.emailOrPhone')),
				})
				.test(
					'phone',
					() => t('errorMessage.phone'),
					(phone, context) => {
						const countryCodeId = context.parent.countryCodeId;
						const email = context.parent.email;
						console.log(123);
						return (
							(phone?.length == 8 && !!countryCodeId) ||
							phone?.length == 0 ||
							(!!phone && !!countryCodeId)
						);
					}
				),

			gender: Yup.string().required(t('errorMessage.pleaseSelect')),
			ageGroup: Yup.string().required(t('errorMessage.pleaseSelect')),
			hasMeditationHabit: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.oneOf(['true', 'false'], t('errorMessage.pleaseSelect')),
			receivePromotion: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.oneOf(['true', 'false'], t('errorMessage.pleaseSelect')),
		},
		[['phone', 'email']]
	);
	const methods = useForm<UserRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchCountryCodeId = methods.watch('countryCodeId');
	const watchEmail = methods.watch('email');
	const watchPhone = methods.watch('phone');
	const watchMonthOfBirth = methods.watch('monthOfBirth');

	const getDay = () => {
		if (!watchMonthOfBirth) return [];
		if ([1, 3, 5, 7, 8, 10, 12].includes(watchMonthOfBirth)) {
			return [...Array(31).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}

		if ([4, 6, 9, 11].includes(watchMonthOfBirth)) {
			return [...Array(30).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}

		if ([2].includes(watchMonthOfBirth)) {
			return [...Array(29).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}
	};

	useEffect(() => {
		if (watchCountryCodeId || watchEmail) methods.trigger('phone');
		if (watchPhone || watchCountryCodeId) methods.trigger('email');
	}, [watchCountryCodeId, watchEmail, watchPhone]);
	useEffect(() => {
		methods.setValue('dayOfBirth', undefined);
	}, [watchMonthOfBirth]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<UserRequest> = async (data) => {
		const obj = { ...data };
		if (avatarFileObj) obj.avatar = avatarFileObj;
		const response = await createUser(obj);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.userCreateSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate('/user-manage/user-list');
		}
	};

	const handleClose = () => {
		navigate('/user-manage/user-list');
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			setAvatarFileObj(res);
			// methods.setValue('audioFileKey', res.name);
			// methods.trigger('audioFileKey');
			// setAudioFileKey(res);
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h6'>{t('user.userData')}</Typography>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='nickname'
									label={t('user.nickName')}
									maxLength={10}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='password'
									label={t('user.password')}
								/>
							</Grid>
							<Grid item xs={12} sx={{ position: 'relative', top: 20 }}>
								<Typography variant='body2' color='common.neutral40'>
									{t('user.oneContact')}:
								</Typography>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									inputType={InputType.number}
									maxLength={20}
									control={methods.control}
									name='phone'
									label={t('user.phoneNumber')}
									InputProps={{
										startAdornment: (
											<AppDropDownMenu
												control={methods.control}
												formControlStyle={{
													minWidth: 100,
													height: '100%',
												}}
												selectStyle={{
													backgroundColor: 'transparent',
												}}
												isFullWidth={false}
												isFormHelperText={false}
												name='countryCodeId'
												options={(countryList?.data || []).map((item: any) => {
													return {
														name: item.countryCode,
														id: item.countryCodeId,
													};
												})}
												required
											/>
										),
									}}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='email'
									label={t('user.email')}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name='gender'
									label={t('user.sex')}
									options={sexList}
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name='ageGroup'
									label={t('user.ageGroup')}
									options={ageList}
									required
								/>
							</Grid>
							<Grid item xs={3}>
								<Typography variant='caption'>{t('user.birthday')}</Typography>
								<AppDropDownMenu
									control={methods.control}
									name='monthOfBirth'
									label={t('user.month')}
									options={[...Array(12).keys()].map((i) => ({
										id: i + 1,
										name: `${i + 1}`,
									}))}
									required
								/>
							</Grid>
							<Grid item xs={3}>
								<div>&nbsp;</div>
								<AppDropDownMenu
									control={methods.control}
									name='dayOfBirth'
									label={t('user.day')}
									options={getDay() || []}
									required
								/>
							</Grid>
							<Grid item xs={6}></Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('user.isMeditation')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='hasMeditationHabit'
									row
									options={[
										{ id: true, name: t('common.yes') },
										{ id: false, name: t('common.no') },
									]}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('user.isReceiveEmail')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='receivePromotion'
									row
									options={[
										{ id: true, name: t('common.yes') },
										{ id: false, name: t('common.no') },
									]}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('user.customAvatar')}
								</Typography>
								<Box display={'flex'} alignItems={'center'}>
									<img
										src={avatarFileObj?.url ? avatarFileObj?.url : avatar}
										width={80}
										height={80}
										style={{ borderRadius: '50%' }}
									/>
									<Button
										component='label'
										sx={{ height: 24, marginLeft: '24px' }}
									>
										{t('common.uploadImg')}
										<VisuallyHiddenInput
											type='file'
											name='thumbnail'
											accept='image/*'
											onChange={handleUpload}
										/>
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
